import type { Course } from './course';
import { TUNING } from './tuning';
import type { SimState } from './sim';

/**
 * ランキングは 3 本立てにする。1 本にすると報われる遊び方が 1 つになるため。
 *   fastest  … 宝を捨てて突っ切る
 *   treasure … 時間を気にせず集める
 *   overall  … 両立を狙う本命
 *
 * 総合スコアは「稼いだ点 × タイム倍率」の乗算にしている。
 * 当初は `max(0, 基準タイム − 実タイム) × k` を足す加算式だったが、
 * 加算だと 2 つの問題が出た。
 *
 *   1. 線形な項の足し算なので、必ず「最速に振る」か「全回収に振る」の
 *      どちらか一方の極が最適になり、両立を狙う遊び方が最適手にならない。
 *   2. 基準タイムを超えた走行ではタイムボーナスが 0 に張り付き、
 *      それ以降タイムがスコアにまったく影響しなくなる。
 *
 * 乗算なら両方が解ける。遅い走行でも 1 秒縮めれば必ずスコアが上がり、
 * 宝を積むほどタイムを縮める価値も上がる。
 */
export interface ScoreBreakdown {
  readonly timeSeconds: number;
  /** 帰還そのものの価値。宝を 1 つも取らなくてもスコアが出る。 */
  readonly finishBonus: number;
  readonly treasurePoints: number;
  readonly comboBonus: number;
  /** 連れ帰った子ガメと、その加点。 */
  readonly hatchlings: number;
  readonly hatchlingBonus: number;
  readonly bestCombo: number;
  /** 基準タイム ÷ 実タイム。1.0 が基準ちょうど。 */
  readonly timeMultiplier: number;
  readonly total: number;
}

export function scoreRun(state: SimState, course: Course): ScoreBreakdown {
  const stage = course.stage;
  const timeSeconds = Math.max(TUNING.dt, state.t);
  const comboBonus = Math.round(
    Math.pow(state.bestCombo, TUNING.comboExponent) * TUNING.comboMultiplier,
  );
  const timeMultiplier = stage.baseTime / timeSeconds;
  const hatchlingBonus = state.hatchlings * TUNING.hatchlingBonus;
  const earned = TUNING.finishBonus + state.points + comboBonus + hatchlingBonus;

  return {
    timeSeconds,
    finishBonus: TUNING.finishBonus,
    treasurePoints: state.points,
    comboBonus,
    hatchlings: state.hatchlings,
    hatchlingBonus,
    bestCombo: state.bestCombo,
    timeMultiplier,
    total: Math.round(earned * timeMultiplier * stage.difficulty),
  };
}

export function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  const cs = Math.floor((seconds % 1) * 100);
  return `${m}:${String(s).padStart(2, '0')}.${String(cs).padStart(2, '0')}`;
}
